import React, { useEffect, useState } from 'react'
import {
  Button,
  Flex,
  HStack,
  Stack,
  Text,
} from '@chakra-ui/react'

import { IComboVariant, ISavedModifier } from '@/_types'
import { IProduct } from '@/entities/product/model/types'
import { IModifierQuantityObj, IRedux } from '@/_store/types'
import { useTranslations } from 'next-intl'
import Modifiers from '../../../../../Modifiers'
import { useCustomLocale } from '@/_hooks/useCustomLocale'
import { useSelector } from 'react-redux'
import NumberToPrice from '@/_components/NumberToPrice'
import { usePathname } from '@/i18n/navigation'
import { useActiveBrand } from '@/_hooks/useActiveBrand'

interface IProps {
  data: IProduct | null | undefined
  onAdd: (e: any) => void
  selectedVariant: IComboVariant
}

function Details({ selectedVariant, data, onAdd }: IProps) {
  const [modifiersPrice, setModifiersPrice] = useState(0)
  const [modifiersQuantity, setModifiersQuantity] = useState<
    IModifierQuantityObj[]
  >([])
  const [orderModifiers, setOrderModifiers] = useState<ISavedModifier[]>([])
  const [compulsories, setCompulsories] = useState<ISavedModifier[]>([])
  const [expectedModifier, setExpectedModifier] = useState<string[]>([])

  const t = useTranslations()
  const pathname = usePathname()
  const currentLocale = useCustomLocale()
  const { is_hall, menu_id } = useSelector((state: IRedux) => state.session)
  const { branches } = useSelector((state: IRedux) => state.common)
  const shipperId = useActiveBrand()
  const branch = branches?.[shipperId]

  const onOrderClick = () => {
    if (data?.id) {
      onAdd(orderModifiers)
    }
  }

  useEffect(() => {
    if (orderModifiers) {
      let sum = 0
      for (const modifier of orderModifiers) {
        sum += modifier.modifier_price * modifier.modifier_quantity
      }
      setModifiersPrice(sum)
    }
  }, [orderModifiers])

  useEffect(() => {
    if (selectedVariant?.order_modifiers?.length > 0) {
      setOrderModifiers(selectedVariant?.order_modifiers)
    }
  }, [selectedVariant])

  if (!data) {
    return null
  }

  const shouldShow =
    is_hall === 'true' || pathname.endsWith('/menu')
      ? menu_id !== 'null' && Boolean(menu_id && data.active_in_menu)
      : (branch?.menu_id && data.active_in_menu) || !branch?.menu_id

  return (
    <Stack borderColor='gray.200' gap={0} justify='space-between' flex={1}>
      <Stack
        // maxH={{
        //   md:
        //     data?.ingredients?.length > 0 && data?.allergens?.[currentLocale]
        //       ? '500px'
        //       : '370px',
        // }}
        overflowY='auto'
        position='relative'
        gap={4}
        pr={{ md: 2 }}
        pb={{ md: 2 }}
        px={{ base: 4, lg: 0 }}
      >
        <Modifiers
          productInCart={null}
          expectedModifiers={expectedModifier}
          product={data}
          quantity={1}
          onSaveCompulsories={(e: any[]) => setCompulsories(e)}
          orderModifiers={orderModifiers}
          modifiersQuantity={modifiersQuantity}
          setOrderModifiers={setOrderModifiers}
          setModifiersQuantity={setModifiersQuantity}
        />
      </Stack>
      <Stack
        position='sticky'
        bottom={0}
        left={0}
        bg='background.card'
        color='text'
        zIndex={10}
        w='full'
        gap={{ base: 4, lg: 6 }}
        pt={4}
        pb={{ base: 4, lg: 2 }}
        px={{ base: 4, lg: 0 }}
        borderTop='1px solid #00000014'
        boxShadow={{ base: '0 -4px 6px -2px #00000014', lg: 'none' }}
      >
        <Flex
          w='full'
          justifyContent='space-between'
          fontSize='sm'
          fontWeight={600}
        >
          <Text fontSize={{ base: 'sm', lg: 'xl' }} fontWeight={500} flex={1}>
            {data?.title?.[currentLocale]}
          </Text>

          <HStack
            fontSize={{ base: 'sm', lg: '2xl' }}
            fontWeight={600}
            color='text'
          >
            <p>+</p>
            <NumberToPrice
              value={modifiersPrice}
              unstyled
            />
          </HStack>
        </Flex>
        <Flex gap={4} w='full'>
          {shouldShow ? (
            <Button
              onClick={() => onOrderClick()}
              flex={1}
              bg='product_button'
              color='product_button.text'
              _hover={{ bg: 'product_button.hover' }}
              rounded={16}
              fontWeight={600}
              disabled={!data}
              fontSize={{ base: 'sm', lg: 'xl' }}
              height={{ base: '52px', lg: '56px' }}
              loading={data?.has_modifier && !orderModifiers.length}
            >
              {t('add_to_cart')}
            </Button>
          ) : (
            <Button variant='subtle' disabled flex={1}>
              {t('will_be_later')}
            </Button>
          )}
        </Flex>
      </Stack>
    </Stack>
  )
}

export default Details
